import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { isNull } from '@ricoh/ria.client.core/utils/data/object';

import type { FormatDataValueOptions } from '~/common/logic/common';
import type { DataValue, DataValueNull } from '~/common/model/api/common';

import { getListDataType } from './_type';

/**
 * Check is DataValue Null.
 *
 * @param {unknown} value Value to check.
 *
 * @returns {boolean} Value is DataValue Null.
 */
export function isDataValueNull(value: unknown): value is Nullable<DataValueNull> {
  if (isNull(value)) {
    return true;
  }

  if (typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  if (Object.keys(value).length === 0) {
    return true;
  }

  return 'type' in value && !value.type;
}

/**
 * Format DataValue.
 *
 * @param {DataValue | DataValueNull} value DataValue.
 * @param {FormatDataValueOptions} options Options.
 *
 * @returns {string} Formatted DataValue.
 */
export function formatDataValue(value: DataValue | DataValueNull, options: FormatDataValueOptions): string {
  const {
    booleanDisplay: [trueDisplay, falseDisplay],
    isLocalDateTime,
  } = options;

  if (!value.type || isNull(value.value)) {
    return '';
  }

  const listFieldType = getListDataType(value.type);
  if (listFieldType && Array.isArray(value.value)) {
    return value.value
      .map((item) =>
        formatDataValue(
          {
            type: listFieldType,
            value: item,
          } as DataValue,
          options,
        ),
      )
      .join(', ');
  }

  switch (value.type) {
    case 'text':
    case 'integer':
    case 'decimal':
    case 'currency': {
      return value.value.toString();
    }

    case 'boolean': {
      return value.value ? trueDisplay : falseDisplay;
    }

    case 'datetime': {
      return DateTime.nullable(value.value, isLocalDateTime)?.format(DateTime.config.format.display.datetime) ?? '';
    }

    case 'date': {
      return DateTime.nullablePattern({ date: value.value })?.format(DateTime.config.format.display.date) ?? '';
    }

    case 'time': {
      return DateTime.nullablePattern({ time: value.value })?.format(DateTime.config.format.display.time) ?? '';
    }

    case 'table': {
      return JSON.stringify(value.value);
    }

    default: {
      return '';
    }
  }
}
